import { fetchWrapper, getEndpoint } from '../utils';
import Cookie from 'js-cookie';
import { useLocalStorage } from '@rehooks/local-storage';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useState } from 'react';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';

export type ZuoraUserPreferences = {
  reinvented: boolean;
  darkMode: boolean;
  density: 'default' | 'comfortable';
  userSettings?: string;
  globalNavSettings?: Record<string, any>;
  exceededSystemLimit?: boolean;
};

export const mergeUserPreferences = (
  current: ZuoraUserPreferences | undefined,
  patch: Partial<ZuoraUserPreferences>,
): ZuoraUserPreferences =>
  ({
    ...current,
    ...patch,
  } as ZuoraUserPreferences);

const USER_PREFERENCES_QUERY_KEY = 'UserPreferences';

// TODO: Capture Sentry events for API failures
export const getUserPreferences = async (): Promise<ZuoraUserPreferences> => {
  const data = await fetchWrapper<{
    userPreference?: ZuoraUserPreferences;
    success: boolean;
  }>(getEndpoint(window.location, NAVIGATION_ENDPOINTS.USER_PREFERENCES));

  if (!data.success || !data.userPreference) {
    throw new Error('Unable to fetch user preferences');
  }

  return data.userPreference;
};

export const useUserPreferences = (
  { enabled }: { enabled: boolean } = { enabled: true },
) => {
  const queryClient = useQueryClient();
  const [hasFreshUserPreferences, setHasFreshUserPreferences] = useState(false);

  const [cachedPreferences, setCachedPreferences] =
    useLocalStorage<ZuoraUserPreferences>(
      `preferences.userId=${Cookie.get('Zuora-User-Id')}`,
    );

  const {
    data,
    isLoading: isUserPreferencesLoading,
    isFetching: isUserPreferencesFetching,
  } = useQuery(USER_PREFERENCES_QUERY_KEY, getUserPreferences, {
    initialData: () => {
      if (cachedPreferences) {
        return cachedPreferences;
      }
    },
    onSuccess: (data) => {
      setCachedPreferences(data);
      setHasFreshUserPreferences(true);
    },
    refetchOnMount: true,
    staleTime: 0,
    enabled,
  });

  const { mutate, mutateAsync } = useMutation(
    async (preferences: Partial<ZuoraUserPreferences>) => {
      // TODO: Capture Sentry events for API failures
      const clonedPreferences = { ...preferences };
      delete clonedPreferences.exceededSystemLimit;
      const res = await window.fetch(
        getEndpoint(window.location, NAVIGATION_ENDPOINTS.USER_PREFERENCES),
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(clonedPreferences),
        },
      );

      if (!res.ok) {
        throw new Error('Unable to set user preferences');
      }

      const text = await res.text();

      try {
        const data = JSON.parse(text) as { success: boolean };
        if (!data.success) {
          throw new Error('Unable to set user preferences');
        }

        return preferences;
      } catch {
        throw new Error(text);
      }
    },
    {
      onMutate: async () => {
        // Cancel any mount/background refetch so an older GET response cannot
        // overwrite the just-persisted preferences after this mutation wins.
        await queryClient.cancelQueries(USER_PREFERENCES_QUERY_KEY);
      },
      onSuccess: (preferences) => {
        const current = queryClient.getQueryData<ZuoraUserPreferences>(
          USER_PREFERENCES_QUERY_KEY,
        );
        const updated = mergeUserPreferences(current, preferences);
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, updated);
        setCachedPreferences(updated);
      },
    },
  );

  return [
    data,
    mutate,
    mutateAsync,
    isUserPreferencesLoading,
    isUserPreferencesFetching,
    hasFreshUserPreferences,
  ] as const;
};
